import React, {CSSProperties, MouseEvent, ReactNode} from 'react';
import {observer} from 'mobx-react';
import {AutoSizer, ScrollPosition, GridCellRangeProps} from 'react-virtualized';
import {classes, stylesheet} from 'typestyle';
import {important, percent, px} from 'csx';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import SpecialActionTableCell from './SpecialActionTableCell';
import {evenRowClassName, oddRowClassName, spinner} from '../../styles/style';
import {Store} from '../../stores/store';
import GeneralSelectorTableStore, {SelectorTableColumnDef, TableFilter, TableHeaderFilterMenuData} from '../../stores/GeneralSelectorTableStore';
import MultiGrid from '../Virtualized/MultiGrid/MultiGrid';
import cellRangeRenderer from './CellRangeRenderer';
import {TableSearch} from './TableSearch';
import {TableSortDirection} from '../../types/Table';
import {isKPI} from '../../kpi';
import {ColumnGroupInfo} from '../../stores/SelectorTableLayout';
import {ElementRectangle, getElementAbsolutePosition} from '../../utility/DomUtils';
import ColumnSelectorFoldout from './ColumnSelectorFoldout';
import {CompareMode} from '../../stores/CompareStore';
import {I18n} from '../../i18n/i18n';
import SortIcon from './SortIcon';
import FilterIcon from './FilterIcon';
import {ChannelStatus} from '../../stores/ChannelStore';
import SearchSelectorTableStore from '../../stores/searchterm/SearchSelectorTableStore';
import {
    BlackColorFaded40,
    BlueColorFaded40,
    BlackColorFaded30,
    BlackColorFaded20,
    CiBlueColor,
    GreyBlueBackgroundColor,
    GreyColor,
    LightBlueBackgroundColor,
    WhiteColor,
} from '../../styles/colors';

class BrowserFeatures {
    private static _stickySupport?: boolean;

    static get positionSticky() {
        if (!this._stickySupport) {
            const el = document.createElement('a');
            el.style.cssText = 'position: sticky';
            this._stickySupport = el.style.position.indexOf('sticky') !== -1;
        }
        return this._stickySupport;
    }
}

function RowCountIndicator() {
    return (
        <div className={wrapperCss.rowCountIndicator}>
            <I18n i18nKey="table:thousandLines" />
        </div>
    );
}

function TableLoadingHover({store}) {
    if (!store.selectorTableStore.showLoadingHover) {
        return null;
    }
    return (
        <div className={wrapperCss.tableLoadingHover}>
            <div className={spinner(60)} />
        </div>
    );
}

const CHANNEL_LABEL_HEIGHT = 5;
const GROUP_HEIGHT = 35;
export const ROW_HEIGHT = 30;
const SUM_ROW_HEIGHT = 40;
const COLUMN_LABEL_HEIGHT = 55;
const CELL_BORDER_SIZE = 1;

const HEADER_COLOR = WhiteColor;
const GROUP_COLOR = CiBlueColor;

type OnToggleColumnSelector = (id: number, rect: ElementRectangle) => void;

// ==================================================
//  HeaderGroup Component
// ==================================================

interface HeaderGroupProps {
    store: Store;
    selectorTableStore: GeneralSelectorTableStore;
    columnIndex: number;
    style: CSSProperties;
    recomputeGridSize: () => void;
    onToggleColumnSelector: OnToggleColumnSelector;
}

@observer
class HeaderGroup extends React.Component<HeaderGroupProps> {
    private ref: React.RefObject<HTMLDivElement>;

    constructor(props) {
        super(props);
        this.ref = React.createRef();
    }

    private renderTableSearch(left, width) {
        const {selectorTableStore, style} = this.props;
        return (
            <div key="group" style={{zIndex: 1}}>
                <div
                    className={classes(wrapperCss.group, wrapperCss.groupLabelContainer)}
                    style={{
                        ...style,
                        left,
                        width,
                        backgroundColor: HEADER_COLOR,
                        paddingLeft: 3,
                        paddingRight: 3,
                    }}>
                    <span className={wrapperCss.groupLabel}>
                        <TableSearch selectorTableStore={selectorTableStore} height={GROUP_HEIGHT - 6} />
                    </span>
                </div>
                <div className={classes(wrapperCss.group, wrapperCss.groupLabelSpacer)} />
            </div>
        );
    }

    private renderGroup(groupInfo: ColumnGroupInfo, left, width) {
        const {selectorTableStore, style} = this.props;
        const layout = selectorTableStore.layout;

        const onToggleClick = () => {
            layout.toggleGroup(groupInfo.id);
            this.props.recomputeGridSize();
        };
        const onSettingsClick = (event) => {
            if (this.ref.current) {
                this.props.onToggleColumnSelector(groupInfo.id, getElementAbsolutePosition(this.ref.current));
            }
            event.stopPropagation();
        };
        let toggleIcon: ReactNode | null = null;
        if (groupInfo.withToggleButton) {
            toggleIcon = (
                <span className={wrapperCss.groupIcon}>
                    <FontAwesomeIcon icon={groupInfo.isOpen ? 'chevron-left' : 'chevron-right'} />
                </span>
            );
        }
        return (
            <div key="group" style={{zIndex: 1}}>
                <div
                    ref={this.ref}
                    className={classes(wrapperCss.group, wrapperCss.groupLabelContainer, wrapperCss.groupClickable)}
                    onClick={onToggleClick}
                    style={{
                        ...style,
                        left: left + 2,
                        width: width - 4,
                        top: 5,
                        paddingRight: 5,
                    }}>
                    <span className={wrapperCss.groupLabel}>{groupInfo.label}</span>
                    <span className={wrapperCss.groupIcon} onClick={onSettingsClick}>
                        <FontAwesomeIcon icon="cog" />
                    </span>
                    {toggleIcon}
                </div>
                <div className={classes(wrapperCss.group, wrapperCss.groupLabelSpacer)} />
            </div>
        );
    }

    public render() {
        const {selectorTableStore, columnIndex} = this.props;
        const layout = selectorTableStore.layout;

        const columnDefs = layout.getVisibleColumnDefs();
        const groupInfos = layout.getColumnGroupInfos();
        const columnWidth = (index: number) => columnDefs[index].width;

        const groupInfo = groupInfos[columnIndex];

        if (groupInfo) {
            let left = 0;
            let width = 0;
            for (let i = groupInfo.firstRel; i <= groupInfo.lastRel; i++) {
                if (i < 0) {
                    left -= columnWidth(columnIndex + i);
                }
                width += columnWidth(columnIndex + i);
            }
            if (columnIndex < layout.getFixedColumnCount()) {
                return this.renderTableSearch(left, width);
            }
            return this.renderGroup(groupInfo, left, width);
        }

        return <div className={classes(wrapperCss.group, wrapperCss.groupLabelSpacer)} />;
    }
}

// ==================================================
//  HeaderLabel Component
// ==================================================

interface HeaderLabelProps {
    store: Store;
    selectorTableStore: GeneralSelectorTableStore;
    columnIndex: number;
    visibleColumnDefs: SelectorTableColumnDef[];
    columnGroupInfo: ColumnGroupInfo[];
}

@observer
export class HeaderLabel extends React.Component<HeaderLabelProps> {
    private openFilter(event: MouseEvent<HTMLDivElement>, columnDef: SelectorTableColumnDef) {
        const {store, selectorTableStore} = this.props;

        event.preventDefault();
        event.stopPropagation();

        const target: Element | null = event.currentTarget.querySelector('svg');
        if (!target) {
            return;
        }
        const rect = getElementAbsolutePosition(target);
        const dataKey = columnDef.dataKey;

        if (!isKPI(dataKey)) {
            throw new Error(`Expected KPI in data-key but was ${dataKey}`);
        }

        const contextMenuInfo: TableHeaderFilterMenuData = {
            x: rect.left - 10,
            y: rect.bottom + 5,
            attribute: dataKey,
            compareColumnInfo: columnDef.compareColumnInfo,
            activeAnalyticsTab: store.getActiveAnalyticsTab(),
        };
        selectorTableStore.triggerTableHeaderFilterMenu(contextMenuInfo);
    }

    private foldOutDetailColumns(columnDef: SelectorTableColumnDef) {
        const {compareStore, selectorTableStore} = this.props.store;
        if (compareStore.allModesVisibleForKpi(columnDef.dataKey)) {
            compareStore.setCompareModeForKpi(columnDef.dataKey, CompareMode.AbsDiffMode, true);
            compareStore.setCompareModeForKpi(columnDef.dataKey, CompareMode.RatioMode, false);
            compareStore.setCompareModeForKpi(columnDef.dataKey, CompareMode.ChannelOrVariantColumn, false);
        } else {
            compareStore.setCompareModeForKpi(columnDef.dataKey, CompareMode.AbsDiffMode, true);
            compareStore.setCompareModeForKpi(columnDef.dataKey, CompareMode.RatioMode, true);
            compareStore.setCompareModeForKpi(columnDef.dataKey, CompareMode.ChannelOrVariantColumn, true);
        }

        selectorTableStore.layout.updateVisibleColumns();
        selectorTableStore.recomputeMultiGridSizeIfPossible();
    }

    private checkIfColumnIsSortedInChannelCompareMode(columnDef: SelectorTableColumnDef, compareChannel?: string): boolean {
        const {compareStore} = this.props.store;
        const {tableSorting} = this.props.store.selectorTableStore;

        if (!compareStore.compareIsEnabled) {
            return true;
        }

        const {compareColumnInfo} = columnDef;
        if (compareColumnInfo) {
            switch (compareColumnInfo.compareMode) {
                case CompareMode.ChannelOrVariantColumn:
                    return compareChannel !== undefined && compareChannel === compareColumnInfo.targetName;
                case CompareMode.RatioMode:
                    return tableSorting.compareMode === CompareMode.RatioMode;
                case CompareMode.AbsDiffMode:
                    return tableSorting.compareMode === CompareMode.AbsDiffMode;
                default:
                    break;
            }
        }

        // if no result until now and tableSorting is set, false
        return !tableSorting.compareMode;
    }

    private renderSortIcon(columnDef: SelectorTableColumnDef): React.ReactNode {
        const {
            store: {compareStore},
        } = this.props;

        const {compareEnabled, channelCompare} = compareStore.compareData;

        if (columnDef.isSortable !== undefined && !columnDef.isSortable) {
            return null;
        }

        const compareChannels = channelCompare ? channelCompare.channelNames : [];

        if (
            compareEnabled &&
            compareChannels.length > 2 &&
            columnDef.compareColumnInfo &&
            columnDef.compareColumnInfo.compareMode === CompareMode.RatioMode
        ) {
            return null;
        }

        const {selectorTableStore} = this.props;
        let sortActive = false;
        let sortDirection = TableSortDirection.DESC;
        const sortAttribute = selectorTableStore.tableSorting.attribute;

        const isSortedByScoreAttribute =
            columnDef.scoreAttributes &&
            (sortAttribute === columnDef.scoreAttributes.outlier ||
                sortAttribute === columnDef.scoreAttributes.minSlope ||
                sortAttribute === columnDef.scoreAttributes.maxSlope);

        if (
            (sortAttribute === columnDef.dataKey || isSortedByScoreAttribute) &&
            this.checkIfColumnIsSortedInChannelCompareMode(columnDef, selectorTableStore.tableSorting.targetName)
        ) {
            sortActive = true;
            sortDirection = this.props.store.selectorTableStore.tableSorting.sortDirection;
        }

        const isChannelCompareDiffCell = columnDef.compareColumnInfo && columnDef.compareColumnInfo.compareMode === CompareMode.AbsDiffMode;

        return (
            <div
                className={classes(
                    wrapperCss.columnLabelClickable,
                    isChannelCompareDiffCell ? wrapperCss.columnLabelClickableLeftThird : wrapperCss.columnLabelClickableLeft
                )}
                onClick={() =>
                    selectorTableStore.setTableSort(
                        columnDef.dataKey,
                        'TOGGLE',
                        columnDef.compareColumnInfo ? columnDef.compareColumnInfo.compareMode : undefined,
                        columnDef.compareColumnInfo && columnDef.compareColumnInfo.targetName ? columnDef.compareColumnInfo.targetName : undefined
                    )
                }>
                <SortIcon sortActive={sortActive} sortByScoreAttributeActive={isSortedByScoreAttribute} sortDirection={sortDirection} />
            </div>
        );
    }

    private filterActive(filter: TableFilter, columnDef: SelectorTableColumnDef) {
        const matchesChannelCompareChannel =
            filter.compareColumnInfo && filter.compareColumnInfo.targetName && columnDef.compareColumnInfo && columnDef.compareColumnInfo.targetName
                ? filter.compareColumnInfo.targetName === columnDef.compareColumnInfo.targetName
                : false;

        const matchesChannelCompareMode =
            filter.compareColumnInfo && columnDef.compareColumnInfo
                ? filter.compareColumnInfo.compareMode === columnDef.compareColumnInfo.compareMode
                : false;

        if (columnDef.compareColumnInfo && columnDef.compareColumnInfo.compareMode) {
            switch (columnDef.compareColumnInfo.compareMode) {
                case CompareMode.ChannelOrVariantColumn:
                    return filter.attribute === columnDef.dataKey && matchesChannelCompareChannel;
                case CompareMode.AbsDiffMode:
                    return filter.attribute === columnDef.dataKey && matchesChannelCompareMode;
                default:
                    return false;
            }
        }
        return filter.attribute === columnDef.dataKey;
    }

    private renderFilterIcon(columnDef: SelectorTableColumnDef): React.ReactNode {
        const {selectorTableStore} = this.props;
        if (!columnDef.withFilter) {
            return null;
        }

        let filterActive = false;
        if (selectorTableStore.activeTableFilters.some((filter) => this.filterActive(filter, columnDef))) {
            filterActive = true;
        }

        const {compareIsEnabled} = this.props.store.compareStore;

        if (compareIsEnabled && columnDef.compareColumnInfo && columnDef.compareColumnInfo.compareMode === CompareMode.RatioMode) {
            return null;
        }

        const isChannelCompareDiffHeaderCell = columnDef.compareColumnInfo && columnDef.compareColumnInfo.compareMode === CompareMode.AbsDiffMode;

        return (
            <div
                className={classes(
                    'ignore-react-onclickoutside',
                    wrapperCss.columnLabelClickable,
                    isChannelCompareDiffHeaderCell ? wrapperCss.columnLabelClickableCenterThird : wrapperCss.columnLabelClickableRight
                )}
                onClick={(event) => this.openFilter(event, columnDef)}
                data-key={columnDef.dataKey}>
                <FilterIcon filterActive={filterActive} />
            </div>
        );
    }

    private getModeOrChannelNameIfChannelCompareIsActive(
        columnDef: SelectorTableColumnDef
    ): {
        channelCompareTitleAddition: string | React.ReactNode;
        columnFoldoutButton: React.ReactNode;
        noBorderLeft: boolean;
        noBorderRight: boolean;
        showCompareTitleAdditionAndColumnAttribute: boolean;
    } {
        const {compareStore} = this.props.store;
        if (!columnDef.compareColumnInfo) {
            return {
                channelCompareTitleAddition: null,
                columnFoldoutButton: null,
                noBorderLeft: false,
                noBorderRight: false,
                showCompareTitleAdditionAndColumnAttribute: false,
            };
        }

        if (!compareStore.checkIfColumnShouldBeAffectedByChannelComparison(columnDef.dataKey)) {
            return {
                channelCompareTitleAddition: null,
                columnFoldoutButton: null,
                noBorderLeft: false,
                noBorderRight: false,
                showCompareTitleAdditionAndColumnAttribute: false,
            };
        }

        const compareColumnsAreFoldedOut = compareStore.allModesVisibleForKpi(columnDef.dataKey);
        const comparedChannels = compareStore.compareData.channelCompare ? compareStore.compareData.channelCompare.channelNames : [];
        const lastComparedChannel = comparedChannels[comparedChannels.length - 1];

        switch (columnDef.compareColumnInfo.compareMode) {
            case CompareMode.AbsDiffMode:
                return {
                    channelCompareTitleAddition: <I18n i18nKey="table:difference" />,
                    columnFoldoutButton: (
                        <div className={wrapperCss.compareModeToggle} onClick={() => this.foldOutDetailColumns(columnDef)}>
                            <div className={wrapperCss.centerIcon}>
                                <FontAwesomeIcon icon={compareColumnsAreFoldedOut ? 'chevron-left' : 'chevron-right'} />
                            </div>
                        </div>
                    ),
                    noBorderLeft: false,
                    noBorderRight: compareColumnsAreFoldedOut,
                    showCompareTitleAdditionAndColumnAttribute: true,
                };
            case CompareMode.RatioMode:
                return {
                    channelCompareTitleAddition: <I18n i18nKey="table:compare" />,
                    columnFoldoutButton: null,
                    noBorderLeft: true,
                    noBorderRight: true,
                    showCompareTitleAdditionAndColumnAttribute: false,
                };
            case CompareMode.ChannelOrVariantColumn:
                return {
                    channelCompareTitleAddition: columnDef.compareColumnInfo.targetName,
                    columnFoldoutButton: null,
                    noBorderLeft: true,
                    noBorderRight: columnDef.compareColumnInfo.targetName !== lastComparedChannel,
                    showCompareTitleAdditionAndColumnAttribute: false,
                };
            default:
                return {
                    channelCompareTitleAddition: null,
                    columnFoldoutButton: null,
                    noBorderLeft: false,
                    noBorderRight: false,
                    showCompareTitleAdditionAndColumnAttribute: false,
                };
        }
    }

    public render() {
        const {columnIndex, visibleColumnDefs, columnGroupInfo} = this.props;
        const columnDef = visibleColumnDefs[columnIndex];

        if (!columnDef) {
            return null;
        }

        const {
            channelCompareTitleAddition,
            columnFoldoutButton,
            noBorderLeft,
            noBorderRight,
            showCompareTitleAdditionAndColumnAttribute,
        } = this.getModeOrChannelNameIfChannelCompareIsActive(columnDef);

        let labelClassName = wrapperCss.columnLabel;

        if (visibleColumnDefs[columnIndex] && !columnGroupInfo[columnIndex].isVisible) {
            labelClassName = classes(labelClassName, wrapperCss.columnLabelIsHidden);
        }

        let columnTitle = channelCompareTitleAddition || columnDef.label;
        if (showCompareTitleAdditionAndColumnAttribute) {
            columnTitle = columnDef.label;
        }

        return (
            <div
                className={classes(
                    wrapperCss.columnLabelContainer,
                    noBorderLeft ? 'noBorderLeft' : undefined,
                    noBorderRight ? 'noBorderRight' : undefined
                )}>
                <div title={columnDef.description} data-key={columnDef.dataKey} className={labelClassName}>
                    {showCompareTitleAdditionAndColumnAttribute ? (
                        <div>
                            <div className={classes(wrapperCss.columnLabelRow, 'isTitle')}>{columnTitle}</div>
                            <div className={wrapperCss.columnLabelRow}>{channelCompareTitleAddition}</div>
                        </div>
                    ) : (
                        columnTitle
                    )}
                </div>
                {this.renderSortIcon(columnDef)}
                {this.renderFilterIcon(columnDef)}
                {columnFoldoutButton ? (
                    <div className={classes(wrapperCss.columnLabelClickable, wrapperCss.columnAbButton)}>{columnFoldoutButton}</div>
                ) : null}
            </div>
        );
    }
}

// ==================================================
//  HeaderSum Component
// ==================================================

interface HeaderSumProps {
    store: Store;
    selectorTableStore: GeneralSelectorTableStore;
    columnIndex: number;
    visibleColumnDefs: SelectorTableColumnDef[];
}

@observer
class HeaderSum extends React.Component<HeaderSumProps> {
    public render() {
        const {store, selectorTableStore, columnIndex, visibleColumnDefs} = this.props;

        // reRender-Signals
        selectorTableStore.updateChannelSumRow;

        const sums = store.channelStore.selectedChannel
            ? selectorTableStore.channelSumRowInformation[store.channelStore.selectedChannel.name]
            : undefined;
        const content = (
            <SpecialActionTableCell
                chartabilityIsAllowed={selectorTableStore.channelSumRowIsChartable}
                isChannel={true}
                store={store}
                columnDef={visibleColumnDefs[columnIndex]}
                rowIndex={-1}
                rowValues={sums || {}}
            />
        );
        return <div className={classes(wrapperCss.cell, wrapperCss.columnSum)}>{content}</div>;
    }
}

// ==================================================
//  Header Component
// ==================================================

interface HeaderProps {
    store: Store;
    selectorTableStore: GeneralSelectorTableStore;
    columnIndex: number;
    style: CSSProperties;
    className: string;
    recomputeGridSize: () => void;
    onToggleColumnSelector: OnToggleColumnSelector;
}

class Header extends React.PureComponent<HeaderProps> {
    public render() {
        const {store, selectorTableStore, columnIndex, style, className, recomputeGridSize, onToggleColumnSelector} = this.props;

        // By setting the zIndex, the columns overlap from front to back.
        // This is needed to detect clicks on group headers that overlap over other columns.

        const visibleColumnDefs = selectorTableStore.layout.getVisibleColumnDefs();
        const columnGroupInfo = selectorTableStore.layout.getColumnGroupInfos();

        if (!visibleColumnDefs || visibleColumnDefs.length === 0 || !visibleColumnDefs[columnIndex]) {
            return null;
        }

        if (!columnGroupInfo || columnGroupInfo.length === 0 || !columnGroupInfo[columnIndex]) {
            return null;
        }

        return (
            <div style={{...style, zIndex: 100 - columnIndex}} className={className}>
                <HeaderGroup
                    store={store}
                    selectorTableStore={selectorTableStore}
                    columnIndex={columnIndex}
                    style={style}
                    recomputeGridSize={recomputeGridSize}
                    onToggleColumnSelector={onToggleColumnSelector}
                />
                <HeaderLabel
                    store={store}
                    selectorTableStore={selectorTableStore}
                    columnIndex={columnIndex}
                    visibleColumnDefs={visibleColumnDefs}
                    columnGroupInfo={columnGroupInfo}
                />
                <HeaderSum store={store} selectorTableStore={selectorTableStore} columnIndex={columnIndex} visibleColumnDefs={visibleColumnDefs} />
            </div>
        );
    }
}

// ==================================================
//  TableWrapper Component
// ==================================================

interface TableWrapperProps {
    store: Store;
    onColumnSelectorVisible: (isVisible: boolean) => void;
}

interface TableWrapperState {
    columnSelectorGroupId: number;
    columnSelectorRect: ElementRectangle | null;
}

@observer
export default class TableWrapper extends React.Component<TableWrapperProps, TableWrapperState> {
    private multiGrid?: MultiGrid;

    public readonly state: TableWrapperState = {
        columnSelectorGroupId: 0,
        columnSelectorRect: null,
    };

    // react-onclickoutside can't stop click propagation properly.
    // We have to debounce clicks to prevent strange behaviour when the user clicks on the settings icon.
    private debounce = 0;

    private setColumnSelector(groupId, rect) {
        if (Date.now() > this.debounce) {
            this.setState({columnSelectorGroupId: groupId, columnSelectorRect: rect});
            this.props.onColumnSelectorVisible(rect !== null);
            this.debounce = Date.now() + 150;
        }
    }

    private closeOverlays = () => {
        this.props.store.selectorTableStore.closeOpenTableDropdowns();
        this.setColumnSelector(0, null);
    };

    private onTableScroll = (scrollInfo: ScrollPosition) => {
        this.closeOverlays();
    };

    private registerMultiGridRef = (ref) => {
        this.multiGrid = ref;
        if (ref) {
            this.props.store.selectorTableStore.setRecomputeMultiGridSize(ref.recomputeGridSize);
        }
    };

    private recomputeMultiGridSize() {
        if (this.multiGrid) {
            this.multiGrid.recomputeGridSize();
        }
    }

    public render() {
        const {
            store,
            store: {channelStore},
            store: {selectorTableStore},
        } = this.props;

        // reRender-Signals
        selectorTableStore.updateTable;

        if (
            channelStore.availableChannels &&
            channelStore.availableChannels.length === 0 &&
            channelStore.channelLoadingStatus === ChannelStatus.Available
        ) {
            // table channel is not set, because no channel data was returned from the server
            return (
                <div className={wrapperCss.message}>
                    <I18n i18nKey={'table:noDataInSelectedRange'} />
                </div>
            );
        }

        const layout = selectorTableStore.layout;
        const columnDefs = layout.getVisibleColumnDefs();

        if (columnDefs.length === 0) {
            return null;
        }

        const fixedColumnCount = layout.getFixedColumnCount();

        if (!channelStore.selectedChannel || selectorTableStore.loadedChannels.indexOf(channelStore.selectedChannel.name) === -1) {
            return (
                <div className={wrapperCss.message}>
                    <I18n i18nKey="data.loading" />
                    <div className={spinner(50)} />
                </div>
            );
        }

        let rows;
        if (selectorTableStore instanceof SearchSelectorTableStore && selectorTableStore.searchActive) {
            rows = selectorTableStore.searchResult;
        } else {
            rows = selectorTableStore.tableData[channelStore.selectedChannel.name];
        }
        if (!rows) {
            rows = [];
        }

        const columnCount = columnDefs.length;

        const columnWidth = (columnIndex) => columnDefs[columnIndex].width;
        const rowHeight = (rowIndex) => (rowIndex ? ROW_HEIGHT : CHANNEL_LABEL_HEIGHT + GROUP_HEIGHT + COLUMN_LABEL_HEIGHT + SUM_ROW_HEIGHT);

        const rowValues = (rowIndex) => {
            if (!rows || rowIndex === 0 || rowIndex - 1 >= rows.length) {
                return {};
            }
            return rows[rowIndex - 1];
        };

        const renderCell_ = (columnIndex, key, rowIndex, style, className) => {
            if (rowIndex === 0) {
                const onToggleColumnSelector = (id, rect) => {
                    if (this.state.columnSelectorRect) {
                        this.setColumnSelector(0, null);
                        return;
                    }
                    this.setColumnSelector(id, rect);
                };
                return (
                    <Header
                        key={key}
                        store={store}
                        selectorTableStore={selectorTableStore}
                        columnIndex={columnIndex}
                        style={style}
                        className={className}
                        recomputeGridSize={() => this.recomputeMultiGridSize()}
                        onToggleColumnSelector={onToggleColumnSelector}
                    />
                );
            }

            className = classes(className, wrapperCss.cell, rowIndex & 1 ? oddRowClassName : evenRowClassName);
            return (
                <div key={key} style={{...style, lineHeight: px(rowHeight(rowIndex) - CELL_BORDER_SIZE * 2)}} className={className}>
                    <SpecialActionTableCell
                        store={store}
                        columnDef={columnDefs[columnIndex]}
                        rowIndex={rowIndex}
                        rowValues={rowValues(rowIndex) || {}}
                    />
                </div>
            );
        };

        const fixedColumnsWidth = () => {
            let width = 0;
            if (BrowserFeatures.positionSticky) {
                for (let i = 0; i < fixedColumnCount; i++) {
                    width += columnWidth(i);
                }
            }
            return width;
        };

        const renderCell = ({columnIndex, key, rowIndex, style}) => {
            if (BrowserFeatures.positionSticky && columnIndex < fixedColumnCount) {
                let left = 0;
                for (let i = 0; i < columnIndex; i++) {
                    left += columnWidth(i);
                }
                const outerStyle = {...style, left, width: 'calc(100% - ' + left + 'px)', zIndex: columnIndex === 0 ? 0 : 100 - columnIndex};
                const innerStyle = {...style, left};
                return (
                    <div className={wrapperCss.fixedCellOuter} key={key + '-c'} style={outerStyle}>
                        {renderCell_(columnIndex, key, rowIndex, innerStyle, wrapperCss.fixedCellInner)}
                    </div>
                );
            }
            return renderCell_(columnIndex, key, rowIndex, style, '');
        };

        const cellRangeRenderer_ = (props: GridCellRangeProps) => {
            return cellRangeRenderer({...props, fixedColumnCount});
        };

        return (
            <div className={wrapperCss.container}>
                <AutoSizer>
                    {(p) => {
                        const width1 = p.width;
                        const height1 = p.height;
                        return (
                            <MultiGrid
                                ref={this.registerMultiGridRef}
                                cellRenderer={renderCell}
                                columnWidth={({index}) => columnWidth(index)}
                                rowHeight={({index}) => rowHeight(index)}
                                columnCount={columnCount}
                                enableFixedColumnScroll={false}
                                enableFixedRowScroll={false}
                                height={height1}
                                rowCount={rows.length + 1}
                                width={width1}
                                hideTopRightGridScrollbar
                                hideBottomLeftGridScrollbar
                                fixedColumnCount={0}
                                fixedRowCount={1}
                                onScroll={this.onTableScroll}
                                cellRangeRenderer={cellRangeRenderer_}
                                fixedColumnsWidth={fixedColumnsWidth}
                            />
                        );
                    }}
                </AutoSizer>
                <TableLoadingHover store={store} />
                <ColumnSelectorFoldout
                    store={store}
                    groupId={this.state.columnSelectorGroupId}
                    rect={this.state.columnSelectorRect}
                    onNotifyLayoutChange={() => this.recomputeMultiGridSize()}
                    onClose={this.closeOverlays}
                />
                <RowCountIndicator />
            </div>
        );
    }
}

const wrapperCss = stylesheet({
    tableLoadingHover: {
        background: BlackColorFaded30,
        width: 70,
        height: 70,
        top: '50%',
        left: '50%',
        borderRadius: 4,
        transform: 'translateX(-50%) translateY(-50%)',
        position: 'absolute',
        zIndex: 4,
    },
    centerIcon: {
        display: 'flex',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    compareModeToggle: {
        display: 'inline-block',
        marginLeft: 3,
        verticalAlign: 'top',
        width: 20,
        height: 20,
        textAlign: 'center',
        borderRadius: 2,
        $nest: {
            '&:hover': {
                backgroundColor: BlackColorFaded20,
            },
        },
    },
    container: {
        position: 'relative',
        flex: 1,
        $nest: {
            '& .ReactVirtualized__Grid__innerScrollContainer': {
                overflow: 'visible !important',
            },
            '& .TopRightGrid_ScrollWrapper': {
                backgroundColor: HEADER_COLOR,
            },
        },
    },
    group: {
        backgroundColor: 'transparent',
        padding: '0px 10px',
        height: important('auto'),
        color: WhiteColor,
        lineHeight: px(GROUP_HEIGHT - 4), // Subtract padding
    },
    groupClickable: {
        cursor: 'pointer',
    },
    groupLabelContainer: {
        backgroundColor: GROUP_COLOR,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderTopLeftRadius: 5,
        borderTopRightRadius: 5,
    },
    groupLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: percent(100),
    },
    groupIcon: {
        padding: '0 5px', // Pad for Fat Fingers
        cursor: 'pointer',
    },
    groupLabelSpacer: {
        minHeight: px(CHANNEL_LABEL_HEIGHT + GROUP_HEIGHT),
    },
    columnLabelContainer: {
        paddingTop: 3,
        paddingLeft: 5,
        height: COLUMN_LABEL_HEIGHT,
        overflow: 'hidden',
        backgroundColor: GreyBlueBackgroundColor,
        position: 'relative',
        border: '1px solid ' + WhiteColor,
        $nest: {
            '&.noBorderLeft': {
                borderLeft: 'none',
            },
            '&.noBorderRight': {
                borderRight: 'none',
            },
        },
    },
    columnAbButton: {
        position: 'absolute',
        right: 0,
        left: '66%',
        fontSize: 12,
    },
    columnSum: {
        height: SUM_ROW_HEIGHT,
        lineHeight: px(SUM_ROW_HEIGHT - CELL_BORDER_SIZE * 2),
        borderBottomColor: important(GreyBlueBackgroundColor),
        backgroundColor: LightBlueBackgroundColor,
    },
    columnLabel: {
        position: 'relative',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: 14,
        height: 25,
        fontvariantId: 'small-caps',
        textAlign: 'center',
    },
    columnLabelRow: {
        lineHeight: px(10),
        fontSize: 12,
        $nest: {
            '&.isTitle': {
                lineHeight: px(15),
            },
        },
    },
    columnLabelIsHidden: {
        color: GreyColor,
    },
    columnLabelClickable: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        paddingBottom: 4,
    },
    columnLabelClickableLeft: {
        left: 0,
        right: '50%',
    },
    columnLabelClickableRight: {
        left: '50%',
        right: 0,
    },
    columnLabelClickableLeftThird: {
        left: 0,
        right: '66%',
    },
    columnLabelClickableCenterThird: {
        left: '33%',
        right: '33%',
    },
    cell: {
        position: 'relative',
        borderColor: WhiteColor,
        borderStyle: 'solid',
        borderWidth: CELL_BORDER_SIZE,
    },
    rowCountIndicator: {
        position: 'absolute',
        right: 30,
        zIndex: 2,
        paddingLeft: 5,
        paddingRight: 5,
        paddingTop: 1,
        paddingBottom: 1,
        bottom: 0,
        background: BlackColorFaded40,
        color: WhiteColor,
        height: 20,
        lineHeight: px(20),
        borderTopRightRadius: 4,
        borderTopLeftRadius: 4,
    },
    fixedCellOuter: {
        backgroundColor: 'transparent',
        pointerEvents: 'none',
    },
    fixedCellInner: {
        position: important('sticky'),
        top: important('auto'),
        pointerEvents: 'all',
    },
    activeFilterBubble: {
        position: 'absolute',
        bottom: 0,
        textAlign: 'center',
        lineHeight: px(15),
        right: 8,
        marginBottom: -4,
        marginRight: -8,
        borderRadius: '100%',
        background: BlueColorFaded40,
        width: 15,
        height: 15,
    },
    message: {
        position: 'relative',
        flex: 1,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
});



// WEBPACK FOOTER //
// ./src/views/Table/TableWrapper.tsx